import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { profanityError } from '@h2h/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useNativeChrome } from '../hooks/useNativeChrome';
import { useAuth } from '../context/AuthContext';
import { db } from '../lib/firebase';
import { cn } from '../lib/cn';
import { AVATAR_TONES } from '../lib/avatar';
import { Avatar } from '../components/ui/bits';
import { AvatarUpload } from '../components/ui/AvatarUpload';
import {
  AuthHeader,
  AuthPage,
  AuthTitle,
  ErrorNote,
  PrimaryButton,
  TextField,
  authMessage
} from '../components/auth/parts';
import { Check } from '../components/ui/icons';

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;
type UStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

// The finishing step of sign-up, shown by the app gate while the signed-in user has
// no completed profile (both email and social sign-ups land here). Collects the public
// identity — display name, @username, avatar tone — then writes the profile doc.
export default function CreateProfile() {
  useNativeChrome();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, completeProfile } = useAuth();

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [username, setUsername] = useState('');
  const [tone, setTone] = useState(0);
  const [photoURL, setPhotoURL] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState<UStatus>('idle');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const seq = useRef(0);
  useEffect(() => {
    const handle = username.trim().toLowerCase();
    if (!handle) {
      setStatus('idle');
      return;
    }
    if (!USERNAME_RE.test(handle)) {
      setStatus('invalid');
      return;
    }
    setStatus('checking');
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      try {
        // Just a hint — the real uniqueness guard is the completeProfile callable
        // (a reservation transaction). We read the single reservation doc rather
        // than running a query: one-shot getDoc reads are reliable inside the
        // native WKWebView, where queries can hang. Race a timeout anyway so a
        // stalled read can never trap the player on "Checking…".
        const snap = await Promise.race([
          getDoc(doc(db, 'usernames', handle)),
          new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000))
        ]);
        if (mine !== seq.current) return;
        if (!snap) {
          setStatus('idle'); // couldn't verify in time — let the submit try
          return;
        }
        const takenByOther = snap.exists() && snap.data()?.userId !== user?.uid;
        setStatus(takenByOther ? 'taken' : 'available');
      } catch {
        if (mine === seq.current) setStatus('idle'); // can't verify — let the submit try
      }
    }, 450);
    return () => clearTimeout(t);
  }, [username, user?.uid]);

  const nameOk = displayName.trim().length >= 2;
  const canSubmit = nameOk && !uploading && (status === 'available' || status === 'idle');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!nameOk) {
      setError('Please enter your name.');
      return;
    }
    const nameProfanity = profanityError(displayName, 'name');
    if (nameProfanity) {
      setError(nameProfanity);
      return;
    }
    if (status === 'invalid') {
      setError('Usernames are 3–20 letters, numbers or underscores.');
      return;
    }
    if (status === 'taken') {
      setError('That username is taken.');
      return;
    }
    const handleProfanity = profanityError(username.trim().toLowerCase(), 'username');
    if (handleProfanity) {
      setError(handleProfanity);
      return;
    }
    setBusy(true);
    try {
      const handle = username.trim().toLowerCase();
      await completeProfile({
        displayName: displayName.trim(),
        username: handle,
        avatarTone: tone,
        photoURL: photoURL ?? undefined
      });
      await qc.invalidateQueries({ queryKey: ['user'] });
      // The app gate now sees a complete profile and renders the game.
    } catch (err) {
      // The callable is the source of truth for uniqueness; surface its verdict.
      const code = (err as { code?: string })?.code ?? '';
      if (code.includes('already-exists')) {
        setStatus('taken');
        setError('That username is taken.');
      } else if (code.includes('invalid-argument')) {
        setError((err as { message?: string })?.message || 'Please check your details.');
      } else {
        setError(authMessage(err));
      }
      setBusy(false);
    }
  }

  async function onBack() {
    navigate('/signup');
  }

  return (
    <AuthPage>
      <AuthHeader
        backLabel="Back"
        onBack={onBack}
        trailing={
          <span className="flex items-center gap-[6px] text-[12px] font-bold text-accent-ink">
            <span className="flex gap-1">
              <span className="h-[6px] w-[6px] rounded-full bg-accent-ink/30" />
              <span className="h-[6px] w-[6px] rounded-full bg-accent-ink/30" />
              <span className="h-[6px] w-[6px] rounded-full bg-accent" />
            </span>
            Last step
          </span>
        }
      />

      <form onSubmit={onSubmit} className="flex flex-1 flex-col">
        <div className="mt-2">
          <AuthTitle
            title="Set up your profile"
            subtitle="This is how your rivals will see you on the ladder."
          />
        </div>

        {/* avatar upload + tone picker */}
        <div className="mt-6 flex flex-col items-center">
          <AvatarUpload
            uid={user?.uid ?? null}
            name={displayName}
            tone={tone}
            src={photoURL}
            size={84}
            onUploaded={setPhotoURL}
            onRemove={() => setPhotoURL(null)}
            onBusyChange={setUploading}
          />
          {!photoURL && (
            <div className="mt-4 flex items-center gap-[10px]">
              {AVATAR_TONES.map((t, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setTone(i)}
                  style={{ backgroundImage: `linear-gradient(135deg, ${t.from}, ${t.to})` }}
                  className={cn(
                    'h-7 w-7 rounded-full transition-transform active:scale-90',
                    i === tone ? 'ring-2 ring-ink ring-offset-2 ring-offset-surface-top' : ''
                  )}
                  aria-label={`Avatar colour ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-col gap-4">
          <TextField
            label="Display name"
            value={displayName}
            onChange={setDisplayName}
            placeholder="Sam Okafor"
            autoComplete="name"
            maxLength={30}
          />
          <div>
            <TextField
              label="Username"
              value={username}
              onChange={(v) => setUsername(v.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase())}
              placeholder="samokafor"
              autoComplete="off"
              maxLength={20}
              prefix={<span className="text-[15px] font-bold text-ink3">@</span>}
              trailing={<UsernameStatus status={status} />}
            />
          </div>
        </div>

        {/* ladder preview */}
        <div className="mt-6">
          <span className="mb-2 block text-[11px] font-bold uppercase tracking-eyebrow text-ink2">
            Ladder preview
          </span>
          <div className="flex items-center gap-3 rounded-2xl bg-night p-[14px] text-white shadow-dark">
            <span className="num w-5 text-center text-[15px] font-extrabold text-white/70">8</span>
            <Avatar name={displayName || 'P'} tone={tone} size={38} src={photoURL} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[14px] font-bold tracking-snug">
                {displayName.trim() || 'Your name'}
              </div>
              <div className="truncate text-[12px] font-medium text-white/55">
                @{username || 'username'} · new player
              </div>
            </div>
            <span className="text-white/40">–</span>
          </div>
        </div>

        {error && <div className="mt-4"><ErrorNote>{error}</ErrorNote></div>}

        <div className="flex-1" />

        <div className="pb-2 pt-6">
          <PrimaryButton type="submit" variant="green" busy={busy} disabled={!canSubmit || busy}>
            Continue to competitions
          </PrimaryButton>
          <p className="mt-3 text-center text-[12px] font-medium text-ink3">
            You can change your name and avatar anytime.
          </p>
        </div>
      </form>
    </AuthPage>
  );
}

function UsernameStatus({ status }: { status: UStatus }) {
  if (status === 'checking')
    return <span className="text-[12px] font-semibold text-ink3">Checking…</span>;
  if (status === 'available')
    return (
      <span className="flex items-center gap-1 text-[12px] font-bold text-accent-ink">
        <Check width={14} height={14} strokeWidth={3} />
        Available
      </span>
    );
  if (status === 'taken')
    return <span className="text-[12px] font-bold text-danger">Taken</span>;
  if (status === 'invalid')
    return <span className="text-[12px] font-semibold text-ink3">3–20 chars</span>;
  return null;
}
